import { AsyncPipe, KeyValuePipe, NgClass, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { NotesSafeHtmlPipe } from '@zero/shared/ui-pipes';
import {
  AssetSearchComponent,
  PositionFilter,
  PositionFilterComponent,
  SortHelper,
  SorterLabelComponent,
} from '@zero/shared/ui-web-components';
import { WatchlistFacade, WatchlistItem, WatchlistVM } from '@zero/shared/util-global-state';
import { Asset, GroupedPositionData, InstrumentDataForPosition, PositionFilterType, WatchlistSettings } from '@zero/shared/util-models';
import { TagsFacade } from '@zero/tags/domain';
import { LinkTagComponent, TagsDropdownComponent } from '@zero/tags/feature-tags/web';
import { LOCAL_STORAGE, StorageService } from 'ngx-webstorage-service';
import { BehaviorSubject, combineLatestWith, filter, map } from 'rxjs';
import { WatchlistItemComponent } from '../watchlist-item/watchlist-item.component';

const SETTINGS_CACHE_KEY = 'watchlist-settings';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'zero-watchlist',
  templateUrl: './watchlist.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AssetSearchComponent,
    PositionFilterComponent,
    NgTemplateOutlet,
    WatchlistItemComponent,
    NgClass,
    SorterLabelComponent,
    FormsModule,
    NgbTooltipModule,
    AsyncPipe,
    KeyValuePipe,
    NotesSafeHtmlPipe,
    TranslocoPipe,
    TagsDropdownComponent,
    LinkTagComponent,
  ],
})
export class WatchlistComponent implements OnInit, OnDestroy {
  public readonly watchlistFacade = inject(WatchlistFacade);
  readonly #destroyRef = inject(DestroyRef);
  readonly #translateService = inject(TranslocoService);
  readonly #activatedRoute = inject(ActivatedRoute);
  readonly #cd = inject(ChangeDetectorRef);
  readonly #tagsFacade = inject(TagsFacade);

  @Input() public set items(items: WatchlistVM[]) {
    this.#items = items;
    this.#sort(this.#items);
    this.filter(this.currentFilter);
  }
  @Input() public compressed: boolean;

  public watchlistViewModel: WatchlistVM[] = [];
  public errorMessage: string;
  public currentlyEditingNote = {
    isin: undefined,
    note: undefined,
  };
  public currentlyEditingEntryQuote: string = undefined;
  public instrumentData: InstrumentDataForPosition[] = [];
  public groupedItems?: GroupedPositionData;
  public sorter: SortHelper;
  public currentFilter?: PositionFilter;
  public currentlyEditingTag?: string;
  #isinsByTags: string[] = [];
  #items: WatchlistVM[] = [];

  @ViewChild('noteTextArea') public note: ElementRef<HTMLTextAreaElement>;

  readonly #watchlistSettings = new BehaviorSubject<WatchlistSettings>({
    notesVisible: false,
    watchlistType: 'entry',
  });
  public readonly watchlistSettings$ = this.#watchlistSettings.asObservable();

  constructor(@Inject(LOCAL_STORAGE) private readonly _storage: StorageService) {}

  public ngOnInit(): void {
    this.sorter = new SortHelper('', false, 'watchlist');
    this.#subscribeToSettings();
    this.#listenToParams();
    this.#loadWatchlistSettings();
    this.#listenToTags();
  }

  public ngOnDestroy(): void {
    this.watchlistFacade.unsubscribeFromWatchlist();
  }

  #listenToTags() {
    this.watchlistFacade.watchlistInitialLoaded$
      .pipe(
        filter((watchlistInitialLoaded) => watchlistInitialLoaded),
        combineLatestWith(this.#tagsFacade.tags$, this.#tagsFacade.activatedTagIds$),
        map(([_, tags, activatedTagIds]) => ({ tags, activatedTagIds })),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe(({ tags, activatedTagIds }) => {
        this.#isinsByTags = activatedTagIds
          ? activatedTagIds.flatMap((activatedTagId) => {
              const relevantTag = tags?.find(({ tagId }) => tagId === activatedTagId);
              return relevantTag?.entities?.length ? relevantTag.entities.map(({ entityId }) => entityId) : [];
            })
          : [];

        this.filter(this.currentFilter);
      });
  }

  #subscribeToSettings() {
    this.watchlistSettings$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((settings) => {
      this.watchlistFacade.unsubscribeFromWatchlist();
      this.watchlistFacade.subscribeToWatchlist(settings.watchlistType === 'entry');
    });
  }

  #loadWatchlistSettings(): void {
    const settings: WatchlistSettings = this._storage.get(SETTINGS_CACHE_KEY);
    if (settings) {
      this.#watchlistSettings.next(settings);
    }
  }

  #updateWatchlistSettings(settings: Partial<WatchlistSettings>): void {
    const currentSettings = this.#watchlistSettings.getValue();
    this.#watchlistSettings.next({ ...currentSettings, ...settings });
    this._storage.set(SETTINGS_CACHE_KEY, this.#watchlistSettings.getValue());
    this.filter(this.currentFilter);
  }

  public toggleNotes(settings: WatchlistSettings): void {
    this.#updateWatchlistSettings({
      notesVisible: !settings.notesVisible,
    });
  }

  public setType(type: 'entry' | 'prev'): void {
    this.#updateWatchlistSettings({
      watchlistType: type,
    });
  }

  public editNote(isin: string, note: string): void {
    this.currentlyEditingEntryQuote = undefined;
    this.currentlyEditingNote = { isin, note };
    this.#cd.detectChanges();
    this.note.nativeElement.focus();
  }

  public editEntryQuote = (isin: string): void => {
    this.#updateWatchlistSettings({
      watchlistType: 'entry',
    });
    this.currentlyEditingEntryQuote = isin;
  };

  public updateEntryQuote = (entryQuote: number): void => {
    this.watchlistFacade.updateWatchlistItemEntryQuote(this.currentlyEditingEntryQuote, entryQuote);
    this.currentlyEditingEntryQuote = undefined;
  };

  #listenToParams() {
    this.#activatedRoute.queryParams.subscribe((params) => params['action'] === 'add' && this.addItem(params['isin']));
  }

  public addItem(isin: string): void {
    this.errorMessage = undefined;
    if (this.#items.find((item) => item.isin === isin)) {
      this.errorMessage = this.#translateService.translate('watchlist.error.existing', {
        isin,
      });
      return;
    }

    this.watchlistFacade.addWatchlistItem(isin);
  }

  public removeItem(item: WatchlistItem): void {
    this.errorMessage = undefined;
    this.watchlistFacade.removeWatchlistItem(item.isin);
  }

  public newAssetSelected(asset: Asset): void {
    this.errorMessage = undefined;
    if (!asset) {
      this.errorMessage = this.#translateService.translate('watchlist.error.not-found');
    } else {
      this.addItem(asset.isin);
    }
  }

  public onSubmitNote(note: string, isin: string): void {
    this.errorMessage = undefined;
    this.watchlistFacade.updateWatchlistItemNote(isin, note);
    this.currentlyEditingNote = undefined;
    this.#updateWatchlistSettings({
      notesVisible: true,
    });
  }

  public up = (data: WatchlistVM): void => this.#reorder(data, -1);
  public down = (data: WatchlistVM): void => this.#reorder(data, 1);

  #reorder(data: WatchlistVM, direction: number): void {
    const { isin } = data;
    const index = this.#items.findIndex((item) => item.isin === isin);
    this.sorter.clearCache();
    this.sorter = new SortHelper('', false, 'watchlist');
    this.watchlistFacade.reorderWatchlistItem(index, index + direction);
    this.watchlistFacade.saveWatchlist();
  }

  #sort(entries: WatchlistVM[]): void {
    if (!this.sorter?.attr) {
      this.watchlistViewModel = [...entries].sort((a, b) => b.wlOrder - a.wlOrder);
    }
  }

  public filter = (positionFilter?: PositionFilter): void => {
    if (!this.#items?.length) {
      return;
    }

    this.currentFilter = positionFilter;
    this.groupedItems = undefined;
    this.sorter?.attr && this.sorter.sortValues(this.#items);

    const tagFilter = (position: { isin: string }) =>
      this.#isinsByTags?.length ? this.#isinsByTags.some((isin) => isin === position.isin) : true;

    switch (positionFilter?.type) {
      case PositionFilterType.ALL:
      case undefined:
        this.watchlistViewModel = [...this.#items].filter(tagFilter);
        break;
      case PositionFilterType.CATEGORIES:
        this.groupedItems = this.#items.filter(tagFilter).reduce(
          (result, item) => ({
            ...result,
            [item.type]: [...(result[item.type] || []), item],
          }),
          {},
        );
        break;
      default:
        this.watchlistViewModel = this.#items.filter(tagFilter).filter((item) => item.type === positionFilter.type);
        break;
    }
    this.instrumentData = this.#items.filter(tagFilter).map(
      (item) =>
        ({
          isin: item.isin,
          type: item.type,
        }) as InstrumentDataForPosition,
    );
  };

  public sortBy(sortAttr: string): void {
    this.sorter.sortBy(this.watchlistViewModel, sortAttr);
    if (this.groupedItems) {
      Object.values(this.groupedItems).forEach((items, index) => {
        this.sorter.sortBy(items, sortAttr, index + 1);
      });
    }
  }
}
